import React, {useState, ChangeEvent, FormEvent, useEffect} from 'react';
import {useAppDispatch, useAppSelector} from "../../../utils/Hooks";
import {useLocation, useNavigate} from "react-router-dom";
import {getCountries} from "../../../store/reducers/CountriesSlice";
import {getServices} from "../../../store/reducers/ServicesSlice";
import {getUsers} from "../../../store/reducers/UsersSlice";
import {Option} from "../../common/form/ElogSelect";
import Autosuggest, {SuggestionsFetchRequestedParams} from "react-autosuggest";
import {apiClient} from "../../../utils/ApiClient";

interface CheckerSearchProps {
    type: string
}

const CheckerSearch: React.FC<CheckerSearchProps> = ({type}: CheckerSearchProps) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {search} = useLocation();

    const usersState = useAppSelector((state) => state.users);
    const countriesState = useAppSelector(state => state.countries);
    const servicesState = useAppSelector((state) => state.services);

    const [startCountry, setStartCountry] = useState<string>('');
    const [user, setUser] = useState<string>('');
    const [serviceType, setServiceType] = useState<string>('');
    const [destinationCountry, setDestinationCountry] = useState<string>('');
    const [perPage, setPerPage] = useState<string>('');
    const [vendor, setVendor] = useState<string>('');
    const [client, setClient] = useState<string>('');
    const [id, setId] = useState<string>('');

    const [clientSuggestions, setClientSuggestions] = useState<Option[]>([]);
    const [vendorSuggestions, setVendorSuggestions] = useState<Option[]>([]);

    useEffect(() => {

        dispatch(getCountries());
        dispatch(getServices(false));
        dispatch(getUsers());

        const urlParams = new URLSearchParams(window.location.search);

        setStartCountry(urlParams.get("start_country") || '');
        setUser(urlParams.get("user") || '');
        setServiceType(urlParams.get("service_type") || '');
        setDestinationCountry(urlParams.get("destination_country") || '');
        setClient(urlParams.get("client") || '');
        setVendor(urlParams.get("vendor") || '');
        setPerPage(urlParams.get("perpage") || '80');
        setId(urlParams.get("id") || '');

    }, []);

    const handleSearch = () => {

        const searchParams = new URLSearchParams();

        searchParams.set("startCountry", startCountry);
        searchParams.set("user", user);
        searchParams.set("serviceType", serviceType);
        searchParams.set("destinationCountry", destinationCountry);
        searchParams.set("perPage", perPage);
        searchParams.set("client", client);
        searchParams.set("vendor", vendor);
        searchParams.set("id", id);

        const newUrl = `/checker/${type}?${searchParams.toString()}`;
        navigate(newUrl);

    };

    const handleExcelDownload = () => {


    };

    const fetchClients = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/client?search=${inputValue}`);
            const clients: Option[] = apiResponse.data.map((client: { id: number; name: string }) => ({
                label: client.name,
                value: client.id.toString(),
            }));
            setClientSuggestions(clients);
        } catch (error) {
            console.error('Fetch clients error:', error);
        }
    };

    const fetchVendors = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/vendor?name=${inputValue}`);
            const clients: Option[] = apiResponse.data.map((vendor: { id: number; name: string }) => ({
                label: vendor.name,
                value: vendor.id.toString(),
            }));
            setVendorSuggestions(clients);
        } catch (error) {
            console.error('Fetch vendors error:', error);
        }
    };

    const onClientSuggestionsFetchRequested = ({value}: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchClients(value);
        } else {
            setClientSuggestions([]);
        }
    };

    const onVendorSuggestionsFetchRequested = ({value}: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchVendors(value);
        } else {
            setVendorSuggestions([]);
        }
    };

    const onClientSuggestionsClearRequested = () => {
        setClientSuggestions([]);
    };

    const onVendorSuggestionsClearRequested = () => {
        setVendorSuggestions([]);
    };

    const onClientSuggestionSelected = (event: React.FormEvent<any>, {suggestion}: { suggestion: Option }) => {
        setClient(suggestion.label);
    };

    const onVendorSuggestionSelected = (event: React.FormEvent<any>, {suggestion}: { suggestion: Option }) => {
        setVendor(suggestion.label);
    };

    const renderSuggestion = (suggestion: Option) => (
        <div>
            {suggestion.label}
        </div>
    );

    const countryOptions: Option[] = [
        {value: "0", label: "Kõik"},
        ...countriesState.countries.map(country => ({value: String(country.id), label: country.name}))
    ];

    const userOptions: Option[] = [
        {value: "-1", label: "Kõik"},
        ...usersState.users
            .filter(user => user.logistician === 1)
            .map(user => ({
                value: user.id.toString(),
                label: `${user.firstname} ${user.lastname}`,
            }))
    ];

    const serviceOptions: Option[] = [
        {value: "0", label: "Kõik"},
        ...servicesState.services.map(service => ({
            value: service.id.toString(),
            label: service.name,
        }))
    ];

    return (
        <div className="filter clear" style={{width: '99%'}}>
            <div className="content" style={{width: '100%'}}>
                <p>
                    <label><strong>Alguskoht:</strong></label>
                    <select
                        name="start_country"
                        className="w150"
                        value={startCountry}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setStartCountry(e.target.value)}
                    >
                        {countryOptions.map(option => (
                            <option key={option.value} value={option.value} selected={startCountry === option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Logistik:</strong></label>
                    <select
                        name="user"
                        className="w150"
                        value={user}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setUser(e.target.value)}
                    >
                        {userOptions.map(option => (
                            <option key={option.value} value={option.value} selected={user === option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Teenuse liik:</strong></label>
                    <select
                        name="service"
                        className="w150"
                        value={serviceType}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setServiceType(e.target.value)}
                    >
                        {serviceOptions.map(option => (
                            <option key={option.value} value={option.value} selected={serviceType === option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </p>
                <p>
                    <label><strong>Sihtkoht:&nbsp;&nbsp;&nbsp;&nbsp;</strong></label>
                    <select
                        name="destination_country"
                        className="w150"
                        value={destinationCountry}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setDestinationCountry(e.target.value)}
                    >
                        {countryOptions.map(option => (
                            <option key={option.value} value={option.value} selected={startCountry === option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Kirjeid lehel:</strong></label>
                    <input
                        style={{width: '50px'}}
                        type="text"
                        name="perpage"
                        value={perPage}
                        onChange={(e) => setPerPage(e.target.value)}
                    />
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>ID:</strong></label>
                    <input
                        style={{width: '90px'}}
                        type="text"
                        name="id"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                    />
                    &nbsp;&nbsp;&nbsp;
                </p>
                <p>
                    <span style={{display: 'inline-flex'}}>
                        <label>
                            <strong>Hankija:&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                        </label>
                        <Autosuggest
                            suggestions={vendorSuggestions}
                            onSuggestionsFetchRequested={onVendorSuggestionsFetchRequested}
                            onSuggestionsClearRequested={onVendorSuggestionsClearRequested}
                            onSuggestionSelected={onVendorSuggestionSelected}
                            getSuggestionValue={(suggestion: Option) => suggestion.label}
                            renderSuggestion={renderSuggestion}
                            inputProps={{
                                value: vendor,
                                onChange: (_, {newValue}) => {
                                    setVendor(newValue);
                                },
                            }}
                            theme={{
                                container: 'react-autosuggest__container',
                                suggestionsContainer: (vendorSuggestions.length > 0)
                                    ? 'react-autosuggest__suggestions-container visible'
                                    : 'react-autosuggest__suggestions-container',
                                suggestionsList: 'react-autosuggest__suggestions-list',
                                suggestion: 'react-autosuggest__suggestion',
                                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                            }}
                        />
                        </span>
                    &nbsp;&nbsp;&nbsp;
                    <span style={{display: 'inline-flex'}}>
                        <label>
                            <strong>Klient:&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                        </label>
                        <Autosuggest
                            suggestions={clientSuggestions}
                            onSuggestionsFetchRequested={onClientSuggestionsFetchRequested}
                            onSuggestionsClearRequested={onClientSuggestionsClearRequested}
                            onSuggestionSelected={onClientSuggestionSelected}
                            getSuggestionValue={(suggestion: Option) => suggestion.label}
                            renderSuggestion={renderSuggestion}
                            inputProps={{
                                value: client,
                                onChange: (_, {newValue}) => {
                                    setClient(newValue);
                                },
                            }}
                            theme={{
                                container: 'react-autosuggest__container',
                                suggestionsContainer: (clientSuggestions.length > 0)
                                    ? 'react-autosuggest__suggestions-container visible'
                                    : 'react-autosuggest__suggestions-container',
                                suggestionsList: 'react-autosuggest__suggestions-list',
                                suggestion: 'react-autosuggest__suggestion',
                                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                            }}
                        />
                        </span>
                </p>
            </div>
            <p className="action">
                <button type="submit" onClick={handleSearch}><span><strong>Otsi</strong></span></button>
            </p>
        </div>
    );
};

export default CheckerSearch;